import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { CSSProperties } from "react";
import { PROXY_LOCATIONS } from "@/lib/constants";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Drops a Prism theme's own backgrounds — every token style carries one, not
 *  just the `pre` — so a highlighted block shows the surface it sits on
 *  instead of the theme's hard-coded editor colour. */
export function stripBg(theme: Record<string, CSSProperties>): Record<string, CSSProperties> {
  return Object.fromEntries(
    Object.entries(theme).map(([k, v]) => [
      k,
      { ...v, background: "transparent", backgroundColor: "transparent" },
    ])
  );
}

export function fmtPrice(n: number): string {
  return n % 1 === 0
    ? n.toLocaleString("en-US")
    : n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function scrollToId(id: string, offset = 96) {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: "smooth" });
}

export function msColor(ms: number) {
  if (ms < 1500) return "#4ade80";
  if (ms < 3000) return "#facc15";
  return "#fb923c";
}

export function cleanHost(raw: string) {
  return (
    raw
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .split("/")[0]
      .split("?")[0] || "example.com"
  );
}

export type ProxyRoute = {
  flag: string;
  city: string | null;
  ip: string | null;
  ms: number;
  status?: number;
};

export function countryToFlag(code: string): string {
  if (!code || code.length !== 2) return "🌐";
  return Array.from(code.toUpperCase())
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
}

export function pickRoute(): ProxyRoute {
  const idx = Math.floor(Math.random() * PROXY_LOCATIONS.length);
  const loc = PROXY_LOCATIONS[idx];
  const jitter = Math.floor((Math.random() - 0.5) * 6);
  return { flag: loc.flag, city: loc.city, ip: loc.ip, ms: loc.ms + jitter };
}

export function validateDomain(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return "Enter a domain to test";
  if (/\s/.test(trimmed)) return "Domain can't contain spaces";

  let domain = trimmed
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "");
  domain = domain.split(/[/?#]/)[0];

  const re = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/;
  if (!re.test(domain)) return "Enter a valid domain like example.com";

  return null;
}
